/**
 * Phase A/B/C — Frontend "dual check" hooks matching backend authz.py.
 *
 * useMe()         → current { user, permissions[], visibleOwnerIds:Set|"*", loading, refresh }
 * useCan(perm)    → boolean — does the current user have `perm` (or "*")
 * useCanRecord(perm, ownerId) → boolean — perm AND ownerId in visibleOwnerIds (or "*")
 *
 * Backed by GET /api/me/permissions which is unified into one round-trip.
 * Falls back to closed-permissions when the fetch is still loading or fails.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const MeContext = createContext(null);

const ADMIN_ROLES = new Set(["admin", "super_admin"]);
const HR_HIERARCHY_BYPASS = new Set(["admin", "super_admin", "hr"]);

export function MeProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: true,
    permissions: new Set(),
    visibleOwnerIds: null, // "*" (unrestricted) or Set<string>
    fetchedAt: null,
  });

  const refresh = useCallback(async () => {
    if (!user || user === false) {
      setState({ loading: false, permissions: new Set(), visibleOwnerIds: null, fetchedAt: null });
      return;
    }
    try {
      const { data } = await api.get("/me/permissions");
      const perms = new Set(data.permissions || []);
      const vo = data.visible_owner_ids === "*"
        ? "*"
        : new Set(data.visible_owner_ids || []);
      setState({ loading: false, permissions: perms, visibleOwnerIds: vo, fetchedAt: Date.now() });
    } catch {
      // On error, fail closed — no permissions, no visibility.
      setState({ loading: false, permissions: new Set(), visibleOwnerIds: new Set(), fetchedAt: null });
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const hasPerm = useCallback((perm) => {
    if (!perm) return true;
    if (user && ADMIN_ROLES.has((user.role || "").toLowerCase())) return true;
    if (state.permissions.has("*")) return true;
    return state.permissions.has(perm);
  }, [user, state.permissions]);

  const canSeeOwner = useCallback((ownerId) => {
    if (!user) return false;
    if (HR_HIERARCHY_BYPASS.has((user.role || "").toLowerCase())) return true;
    if (state.visibleOwnerIds === "*") return true;
    if (!ownerId) return false;
    return state.visibleOwnerIds instanceof Set && state.visibleOwnerIds.has(ownerId);
  }, [user, state.visibleOwnerIds]);

  const value = useMemo(() => ({
    user, loading: state.loading, permissions: state.permissions,
    visibleOwnerIds: state.visibleOwnerIds,
    hasPerm, canSeeOwner, refresh,
  }), [user, state.loading, state.permissions, state.visibleOwnerIds, hasPerm, canSeeOwner, refresh]);

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export const useMe = () => {
  const ctx = useContext(MeContext);
  if (!ctx) {
    // Graceful fallback when MeProvider isn't mounted (e.g. unauthenticated pages).
    return {
      user: null, loading: true, permissions: new Set(), visibleOwnerIds: null,
      hasPerm: () => false, canSeeOwner: () => false, refresh: () => {},
    };
  }
  return ctx;
};

/** useCan("candidate.edit") → boolean */
export const useCan = (perm) => useMe().hasPerm(perm);

/** useCanRecord("candidate.edit", record.assigned_employee_id) → boolean
 *  Convenience for the very common pattern: `permission AND record-in-hierarchy`.
 */
export const useCanRecord = (perm, ownerId) => {
  const { hasPerm, canSeeOwner } = useMe();
  return hasPerm(perm) && canSeeOwner(ownerId);
};
